import {
  Component,
  ComponentInterface,
  h,
  Prop,
} from '@stencil/core';

// import { addMinutes } from 'date-fns/addMinutes';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';

import {
  InterfacePearlChainLeg,
  InterfacePearlChainOrientation,
  InterfacePearlChainStatus,
} from './paf-result-pearl-chain.custom';

import { removeTimezoneFromIsoTimeString } from '../../utils/date-helper';

@Component({
  shadow: true,
  styleUrl: 'paf-result-pearl-chain.scss',
  tag: 'paf-result-pearl-chain',
})

export class PafResultPearlChain implements ComponentInterface {

  @Prop() public orientation!: InterfacePearlChainOrientation;

  /**
   * Stringified array of leg object, contains the individual segments
   * of the journey with their associated travel information.
   * See InterfacePearlChainLeg for properties.
   */
  @Prop() public legs!: string;

  /**
   * A configured date which acts as the current date instead
   * of the real current date. Recommended for testing purposes
   */
  @Prop() public now!: number;

  private _getAllDuration(legs): number {
    return legs?.reduce((sum: number, leg) => {
      const arrivalNoTz = removeTimezoneFromIsoTimeString(leg.arrival);
      const departureNoTz = removeTimezoneFromIsoTimeString(leg.departure);

      if (arrivalNoTz && departureNoTz) {
        return (
          sum +
          differenceInMinutes(
            removeTimezoneFromIsoTimeString(leg.arrival) ?? 0,
            removeTimezoneFromIsoTimeString(leg.departure) ?? 0,
          )
        );
      }

      return sum;

    }, 0);
  }

  private _getRelativeDuration(legs: InterfacePearlChainLeg[], leg: InterfacePearlChainLeg): number {
    const arrivalNoTz = removeTimezoneFromIsoTimeString(leg.arrival);
    const departureNoTz = removeTimezoneFromIsoTimeString(leg.departure);

    if (arrivalNoTz && departureNoTz) {
      const duration = differenceInMinutes(
        removeTimezoneFromIsoTimeString(leg.arrival) ?? 0,
        removeTimezoneFromIsoTimeString(leg.departure) ?? 0,
      );
      const allDurations = this._getAllDuration(legs);

      if (allDurations === 0) {
        return 100;
      }

      return (duration / allDurations) * 100;
    }

    return 0;
  }

  private _getStatus(now, start?: Date, end?: Date): InterfacePearlChainStatus {
    if (
      start &&
      isBefore(start, now) &&
      end &&
      isAfter(end, now)
    ) {
      return 'progress';
    } else if (end && isBefore(end, now)) {
      return 'past';
    }

    return 'future';
  }

  public render(): HTMLDivElement {
    const legsArray: InterfacePearlChainLeg[] = JSON.parse(this.legs);

    const allLegsDisrupted = legsArray?.every((leg) => leg?.cancelled);
    const now = new Date(this.now * 1000);

    /**
     * Check if first leg has a stopStatus
     */
    const departureNotServiced = ((): boolean => (
      Boolean(legsArray && legsArray[0]?.stopPoints &&
        legsArray[0]?.stopPoints[0].stopStatus === 'NOT_SERVICED')))();

    /**
     * Check if last leg has a stopStatus
     */
    const arrivalNotServiced = ((): boolean => {
      const lastLeg = legsArray && legsArray[legsArray.length - 1];
      const stops = lastLeg && lastLeg.stopPoints;

      return Boolean(stops && stops[stops.length - 1].stopStatus === 'NOT_SERVICED');
    })();

    return (
      <div
        class={{
          'pearl-chain': true,
          [`pearl-chain--${this.orientation}`]: true,
          'pearl-chain--disruption': allLegsDisrupted,
        }}
      >
        <span class={{
          'pearl-chain__bullet': true,
          'pearl-chain__bullet--departure-skipped': departureNotServiced,
          'pearl-chain__bullet--start': true,
        }}></span>
        {/* render legs */}
        {legsArray.map((leg) => {

          // duration will be represented in %
          const duration = this._getRelativeDuration(legsArray, leg);
          const departure = removeTimezoneFromIsoTimeString(leg.departure);
          const arrival = removeTimezoneFromIsoTimeString(leg.arrival);

          // get status of the current leg
          const status = this._getStatus(now, departure, arrival);
          const hasProgressStatus = status === 'progress';
          const hasPastStatus = status === 'past';

          const isCancelled =
            leg.cancelled;

          let isDepartureSkipped = false;
          let isArrivalSkipped = false;

          if (leg.stopPoints) {
            isDepartureSkipped = leg.stopPoints[0]?.stopStatus === 'NOT_SERVICED';
            isArrivalSkipped = leg.stopPoints[1]?.stopStatus === 'NOT_SERVICED';
          }

          return (
            <div
              class={{
                'pearl-chain__leg': true,
                'pearl-chain__leg--disruption': isCancelled,
                'pearl-chain__leg--past': hasPastStatus,
                'pearl-chain__leg--progress': hasProgressStatus,
              }}
              style={ this.orientation === 'horizontal'
                ? {
                  '--paf-pearl-chain-leg-width': `${duration}%`,
                }
                : undefined
              }
            >
              <span
                class={{
                  'pearl-chain__stop': true,
                  'pearl-chain__stop--arrival-skipped': isArrivalSkipped,
                  'pearl-chain__stop--departure-skipped': isDepartureSkipped,
                }}
              ></span>
            </div>
          );
        })}
        <span class={{
          'pearl-chain__bullet': true,
          'pearl-chain__bullet--departure-skipped': arrivalNotServiced,
          'pearl-chain__bullet--end': true,
        }}></span>
      </div>
    );
  }
}

